import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';

// ── Palette ──────────────────────────────────────────────────────────────────
const P = {
  bg:      '#f0ede8',
  charcoal:'#2b2b2b',
  red:     '#e63b2e',
  muted:   '#8a8680',
  card:    '#e8e4de',
};

// ── Word Reveal Data ──────────────────────────────────────────────────────────
const STORY_STEPS = [
  { word: 'AUDIT',  sub: 'We tear apart your existing site — speed, UX, broken links, SEO — and map every problem.', num: '01', color: P.charcoal },
  { word: 'DESIGN', sub: 'A bold new direction, built around your brand. Prototyped before a line of code is written.', num: '02', color: P.red },
  { word: 'BUILD',  sub: 'Clean, fast code. Your new site assembled with precision and built to last.', num: '03', color: P.charcoal },
  { word: 'LAUNCH', sub: 'We handle the go-live, then stick around for support, fixes, and optimization.', num: '04', color: P.red },
];

const SERVICES = [
  { num: '01', title: 'Full Revamp',     desc: 'Complete redesign — new layout, new code, new life for your brand online.' },
  { num: '02', title: 'Performance Fix', desc: 'Speed audits, image optimization, and Core Web Vitals improvements.' },
  { num: '03', title: 'UX Overhaul',     desc: 'Simplify navigation, sharpen clarity, reduce bounce, increase conversions.' },
  { num: '04', title: 'Mobile-First',    desc: 'Responsive builds optimized for how people actually browse today.' },
];

// ── Particle Canvas ───────────────────────────────────────────────────────────
function ParticleCanvas({ scrollP = 0 }) {
  const canvasRef = useRef(null);
  const mouse     = useRef({ x: -999, y: -999 });
  const pts       = useRef([]);
  const rafId     = useRef(null);

  const init = useCallback((W, H) => {
    pts.current = Array.from({ length: 65 }, () => ({
      x:         Math.random() * W,
      y:         Math.random() * H,
      vx:        (Math.random() - 0.5) * 0.38,
      vy:        (Math.random() - 0.5) * 0.38,
      r:         Math.random() * 2.5 + 2.5,
      glowPhase: Math.random() * Math.PI * 2,
      glowSpeed: Math.random() * 0.03 + 0.015,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const parent = canvas.parentElement;
    const LINK   = 100;

    const resize = () => {
      canvas.width  = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
      init(canvas.width, canvas.height);
    };

    const draw = () => {
      const { width: W, height: H } = canvas;
      ctx.clearRect(0, 0, W, H);
      const list = pts.current;

      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        p.x += p.vx;  p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;

        // Slow smooth pulse — glowSpeed halved for a gentler blink
        p.glowPhase += p.glowSpeed * 0.4;
        const pulse = (Math.sin(p.glowPhase) + 1) / 2; // smooth 0 → 1

        // Smoothly interpolate dot color between charcoal and red
        const r = Math.round(43  + (230 - 43)  * pulse);
        const g = Math.round(43  + (59  - 43)  * pulse);
        const b = Math.round(43  + (46  - 43)  * pulse);
        const baseAlpha = 0.28 + pulse * 0.55;

        // Soft glow ring that fades in as pulse rises
        if (pulse > 0.4) {
          const glowAlpha = (pulse - 0.4) / 0.6 * 0.12;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(230,59,46,${glowAlpha})`;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${baseAlpha})`;
        ctx.fill();

        for (let j = i + 1; j < list.length; j++) {
          const d = Math.hypot(p.x - list[j].x, p.y - list[j].y);
          if (d < LINK) {
            const a = (1 - d / LINK) * 0.13;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(list[j].x, list[j].y);
            ctx.strokeStyle = `rgba(43,43,43,${a})`;
            ctx.lineWidth   = 0.6;
            ctx.stroke();
          }
        }
      }
      rafId.current = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { mouse.current = { x: -999, y: -999 }; };

    resize();
    draw();
    window.addEventListener('resize', resize);
    parent.addEventListener('mousemove', onMove);
    parent.addEventListener('mouseleave', onLeave);

    return () => {
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('resize', resize);
      parent.removeEventListener('mousemove', onMove);
      parent.removeEventListener('mouseleave', onLeave);
    };
  }, [init]);

  const zoom  = 1 + scrollP * 0.35;          // scale 1 → 1.35 as you scroll
  const fade  = 1 - scrollP * 0.6;           // opacity 1 → 0.4

  return (
      <div style={{ position: 'absolute', inset: 0, transform: `scale(${zoom})`, opacity: fade, transformOrigin: 'center center', willChange: 'transform, opacity' }}>
        <canvas ref={canvasRef} className="cx-particle-canvas" aria-hidden="true" />
      </div>
  );
}

// ── useScrollProgress ─────────────────────────────────────────────────────────
function useScrollProgress(ref) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => {
      const { top, height } = el.getBoundingClientRect();
      const raw = (-top) / (height - window.innerHeight);
      setProgress(Math.max(0, Math.min(1, raw)));
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, [ref]);
  return progress;
}

// ── useInView ─────────────────────────────────────────────────────────────────
function useInView(ref, threshold = 0.2) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setInView(true); },
        { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav({ scrolled, globalProgress }) {
  return (
      <nav className={`cx-nav${scrolled ? ' cx-nav--scrolled' : ''}`}>
        <div className="cx-nav__logo">CLIP<span>X</span></div>
        <div className="cx-nav__links">
          <a href="#services">Services</a>
          <a href="#process">Process</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="cx-nav__progress" style={{ width: `${globalProgress * 100}%` }} />
      </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function HeroSection({ scrollY }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 150); return () => clearTimeout(t); }, []);

  // 0 → 1 over first 500px of scroll
  const scrollP    = Math.min(scrollY / 500, 1);
  const barShifts  = [-60, 40, -30, 50];

  // Text fades out and slides up as you scroll away
  const textOpacity  = Math.max(1 - scrollP * 2.2, 0);
  const textTranslate = scrollP * -60;

  return (
      <section className="cx-hero-scene">
        <div className="cx-hero-sticky">
          <ParticleCanvas scrollP={scrollP} />

          {/* Subtle diagonal bars on top of particles */}
          <div className="cx-bars" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
                <div
                    key={i}
                    className={`cx-bar cx-bar--${i + 1}`}
                    style={{ transform: `rotate(-12deg) translateX(${scrollP * barShifts[i]}px)` }}
                />
            ))}
          </div>

          <div
              className={`cx-hero__content${loaded ? ' cx-hero__content--loaded' : ''}`}
              style={{ opacity: textOpacity, transform: `translateY(${textTranslate}px)` }}
          >
            <p className="cx-eyebrow">Website Revamping</p>
            <h1 className="cx-hero__h1">Your site,<br /><em>rebuilt</em><br />for today.</h1>
            <p className="cx-hero__sub">
              Clipx takes outdated, broken websites and transforms them into
              fast, modern experiences that actually work.
            </p>
            <a href="#contact" className="cx-btn cx-btn--dark">Get a free audit</a>
          </div>

          <div className="cx-scroll-hint" aria-hidden="true" style={{ opacity: Math.max(1 - scrollP * 4, 0) }}>
            <span>Scroll</span>
            <div className="cx-scroll-line" />
          </div>
        </div>
      </section>
  );
}

// ── Word Reveal ───────────────────────────────────────────────────────────────
function WordRevealSection() {
  const sceneRef = useRef(null);
  const progress = useScrollProgress(sceneRef);
  const [visible, setVisible]   = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const prevIdx = useRef(0);

  useEffect(() => {
    const idx = Math.min(Math.floor(progress * STORY_STEPS.length), STORY_STEPS.length - 1);
    if (idx !== prevIdx.current) {
      prevIdx.current = idx;
      setVisible(false);
      setTimeout(() => { setActiveIdx(idx); setVisible(true); }, 180);
    }
  }, [progress]);

  useEffect(() => { const t = setTimeout(() => setVisible(true), 300); return () => clearTimeout(t); }, []);

  const step = STORY_STEPS[activeIdx];

  return (
      <section className="cx-words-scene" id="process" ref={sceneRef}>
        <div className="cx-words-sticky">
          <div className="cx-words__bg-num" aria-hidden="true">{step.num}</div>
          <div className={`cx-words__stage${visible ? ' cx-words__stage--visible' : ''}`}>
            <div className="cx-words__word" style={{ color: step.color }}>{step.word}</div>
            <p className="cx-words__sub">{step.sub}</p>
            <div className="cx-words__dots" aria-hidden="true">
              {STORY_STEPS.map((_, i) => (
                  <div key={i} className={`cx-words__dot${i === activeIdx ? ' cx-words__dot--active' : ''}`} />
              ))}
            </div>
          </div>
        </div>
      </section>
  );
}

// ── Services ──────────────────────────────────────────────────────────────────
function ServicesSection() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.15);
  return (
      <section className={`cx-services${inView ? ' cx-services--revealed' : ''}`} id="services" ref={ref}>
        <div className="cx-services__header">
          <p className="cx-eyebrow cx-eyebrow--red">What we do</p>
          <h2 className="cx-section-title">Our services</h2>
        </div>
        <div className="cx-services__grid">
          {SERVICES.map((s, i) => (
              <div className="cx-service-card" key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="cx-service-card__num">{s.num}</div>
                <div className="cx-service-card__title">{s.title}</div>
                <div className="cx-service-card__desc">{s.desc}</div>
              </div>
          ))}
        </div>
      </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
function CtaBand() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.3);
  return (
      <section className={`cx-cta${inView ? ' cx-cta--revealed' : ''}`} id="contact" ref={ref}>
        <h2 className="cx-cta__title">Ready to stop embarrassing<br />yourself online?</h2>
        <a href="mailto:hello@clipx.ca" className="cx-btn cx-btn--light">Start your revamp</a>
      </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
      <footer className="cx-footer">
        <div className="cx-footer__logo">CLIP<span>X</span></div>
        <p className="cx-footer__copy">© {new Date().getFullYear()} Clipx · clipx.ca</p>
      </footer>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [scrollY,         setScrollY]         = useState(0);
  const [globalProgress,  setGlobalProgress]  = useState(0);
  const [navScrolled,     setNavScrolled]     = useState(false);

  useEffect(() => {
    const handler = () => {
      const sy    = window.scrollY;
      const total = document.body.scrollHeight - window.innerHeight;
      setScrollY(sy);
      setGlobalProgress(total > 0 ? sy / total : 0);
      setNavScrolled(sy > 40);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
      <div className="cx-app">
        <Nav scrolled={navScrolled} globalProgress={globalProgress} />
        <HeroSection scrollY={scrollY} />
        <WordRevealSection />
        <ServicesSection />
        <CtaBand />
        <Footer />
      </div>
  );
}